import { getLocale, t } from "./i18n.ts";
import type {
  DraftLine,
  OrderEvent,
  OrderLine,
  OrderRecord,
  Orders,
  PendingSubmission,
  PrinterConfig,
  Product,
  SettingRow
} from "./types";

export const APP_VERSION = "0.6.6";
export const APP_VERSION_CODE = 100_018;

export function uuid(): string {
  return crypto.randomUUID();
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat(getLocale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2
  }).format(value);
}

export function formatDateTime(timestamp: number): string {
  return new Intl.DateTimeFormat(getLocale(), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(timestamp));
}

export function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("el-GR")
    .replace(/\s+/g, " ")
    .trim();
}

export function splitOptions(value: string): string[] {
  if (!value.trim()) {
    return [];
  }
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export type PricedOption = {
  label: string;
  price: number;
};

/** Reads old comma lists and the new semicolon-separated label=price representation. */
export function splitPricedOptions(value: string): PricedOption[] {
  if (!value.trim()) return [];
  const rows = value.includes("\n")
    ? value.split(/\r?\n/)
    : value.includes(";")
      ? value.split(";")
      : value.includes("=")
        ? value.split(/,(?!\d+\s*(?:,|$))/)
        : value.split(",");
  return rows.map((raw) => raw.trim()).filter(Boolean).map((row) => {
    const at = row.lastIndexOf("=");
    if (at <= 0 || at >= row.length - 1) return { label: row, price: 0 };
    const label = row.slice(0, at).trim();
    const parsed = Number.parseFloat(row.slice(at + 1).trim().replace(",", "."));
    return {
      label: label || row,
      price: Number.isFinite(parsed) ? Math.max(0, parsed) : 0
    };
  });
}

export function encodePricedOptions(options: PricedOption[]): string {
  return options
    .map(({ label, price }) => {
      const clean = label.trim().replace(/[;=\r\n]/g, " ").replace(/\s+/g, " ");
      return clean ? `${clean}=${Math.max(0, Number(price) || 0)}` : "";
    })
    .filter(Boolean)
    .join(";");
}

export function selectedExtrasValue(configured: string, selectedLabels: string[]): string {
  const selected = new Set(selectedLabels);
  return encodePricedOptions(splitPricedOptions(configured).filter((option) => selected.has(option.label)));
}

export function selectedExtrasTotal(configured: string, selectedLabels: string[]): number {
  const selected = new Set(selectedLabels);
  return splitPricedOptions(configured).reduce(
    (total, option) => total + (selected.has(option.label) ? option.price : 0),
    0
  );
}

export function optionLabels(value: string): string[] {
  return splitPricedOptions(value).map((option) => option.label);
}

export function splitVariations(value: string): string[] {
  if (!value.trim()) {
    return [];
  }
  const rows = value.includes("\n")
    ? value.split(/\r?\n/)
    : value.includes(";")
      ? value.split(";")
      : [value];
  return rows.map((item) => item.trim()).filter(Boolean);
}

export type CounterOption = {
  label: string;
  price: number;
};

export function splitCounters(value: string): CounterOption[] {
  if (!value.trim()) {
    return [];
  }
  const rows = value.includes("\n")
    ? value.split(/\r?\n/)
    : value.includes(";")
      ? value.split(";")
      : value.split(/,(?=\s*[^,=|:]+[=|:])/);
  return rows
    .map((row) => row.trim())
    .filter(Boolean)
    .map((row) => {
      const splitAt = Math.max(row.lastIndexOf("="), row.lastIndexOf("|"), row.lastIndexOf(":"));
      if (splitAt <= 0 || splitAt >= row.length - 1) {
        return { label: row, price: 0 };
      }
      const label = row.slice(0, splitAt).trim();
      const parsed = Number.parseFloat(row.slice(splitAt + 1).trim().replace(",", "."));
      return {
        label: label || row,
        price: Number.isFinite(parsed) ? Math.max(0, parsed) : 0
      };
    });
}

export function variationMultiplier(variation: string): number {
  const normalized = variation.trim().replace(",", ".");
  if (!normalized) {
    return 1;
  }
  let whole = 0;
  let fraction = normalized;
  const space = normalized.indexOf(" ");
  if (space > 0 && normalized.includes("/")) {
    whole = Number.parseFloat(normalized.slice(0, space).trim());
    fraction = normalized.slice(space + 1).trim();
  }
  if (fraction.includes("/")) {
    const [numeratorText, denominatorText] = fraction.split("/");
    const numerator = Number.parseFloat(numeratorText);
    const denominator = Number.parseFloat(denominatorText);
    const value = whole + numerator / denominator;
    return Number.isFinite(value) && denominator > 0 && value > 0 ? value : 1;
  }
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

export function draftUnitPrice(
  product: Product,
  variation: string,
  counters: Record<string, number>,
  extras: string[] = []
): number {
  const counterPrices = new Map(splitCounters(product.counter_options).map((item) => [item.label, item.price]));
  const counterTotal = Object.entries(counters).reduce(
    (total, [label, count]) => total + (counterPrices.get(label) ?? 0) * count,
    0
  );
  return product.price * variationMultiplier(variation) + counterTotal + selectedExtrasTotal(product.extras, extras);
}

export function draftName(product: Product, variation: string): string {
  return variation ? `${product.name} - ${variation}` : product.name;
}

export function draftPrintName(product: Product, variation: string): string {
  const base = product.print_name?.trim() || product.name;
  return variation ? `${base} - ${variation}` : base;
}

export function draftKey(productId: number, variation: string): string {
  return `${productId}:${variation}`;
}

export function draftTotal(lines: DraftLine[]): number {
  return lines.reduce((total, line) => total + line.unitPrice * line.quantity, 0);
}

export function counterText(counters: Record<string, number>): string {
  return Object.entries(counters)
    .filter(([, count]) => count > 0)
    .map(([label, count]) => `${count} ${label}`)
    .join(", ");
}

export function isOpenOrder(order: OrderRecord): boolean {
  const status = normalize(order.status);
  return order.closed_at <= 0 && !status.includes("κλειστ") && !status.includes("ακυρ");
}

export function orderUpdatedAt(order: OrderRecord, events: OrderEvent[]): number {
  return Math.max(
    order.created_at,
    order.closed_at,
    ...events.filter((event) => event.order_id === order.id).map((event) => event.created_at)
  );
}

export function latestOpenOrder(orders: Orders | null, tableLabel: string): OrderRecord | null {
  if (!orders) {
    return null;
  }
  return (
    orders.orders
      .filter((order) => order.table_label === tableLabel && isOpenOrder(order))
      .sort((a, b) => b.created_at - a.created_at)[0] ?? null
  );
}

export function pendingOrderRecord(
  pending: PendingSubmission,
  orders: Orders | null
): OrderRecord | null {
  if (!orders) {
    return null;
  }
  const eventOrderId = orders.order_events.find(
    (event) => event.submission_id === pending.submissionId
  )?.order_id;
  const orderId = eventOrderId || pending.orderId;
  return orders.orders.find((order) => order.id === orderId) ?? null;
}

export function pendingTableLabel(pending: PendingSubmission, orders: Orders | null): string {
  return pendingOrderRecord(pending, orders)?.table_label || pending.tableLabel;
}

export function pendingForTable(
  pending: PendingSubmission[],
  orders: Orders | null,
  tableLabel: string
): PendingSubmission | undefined {
  return pending
    .filter((item) => pendingTableLabel(item, orders) === tableLabel)
    .sort((a, b) => b.createdAt - a.createdAt)[0];
}

/* True once the server is known to hold this submission's order. That is what makes it safe to
   start a second order for the same table: the first one can no longer be lost, so a new
   submission cannot turn into a duplicate of it. Until then the button stays a retry. */
export function isOrderRecorded(
  pending: PendingSubmission,
  orders: Orders | null
): boolean {
  return pending.orderId > 0 || Boolean(pendingOrderRecord(pending, orders));
}

export function orderLines(orders: Orders | null, orderId: number): OrderLine[] {
  return orders?.order_items.filter((line) => line.order_id === orderId) ?? [];
}

export type EffectiveOrderLine = OrderLine & {
  source_line_ids: number[];
};

function orderLineKey(line: OrderLine): string {
  return [
    line.product_id,
    line.name,
    line.without_options,
    line.extras,
    line.counters,
    line.comment,
    line.takeaway
  ].join("\u0000");
}

/**
 * Order items form an immutable event ledger: corrections append negative and positive rows.
 * Active-table UI needs the effective products after those deltas are applied.
 */
export function effectiveOrderLines(orders: Orders | null, orderId: number): EffectiveOrderLine[] {
  const grouped = new Map<string, EffectiveOrderLine & { has_unserved: boolean }>();
  const lines = orderLines(orders, orderId).slice().sort((a, b) => a.id - b.id);

  for (const line of lines) {
    const key = orderLineKey(line);
    let current = grouped.get(key);
    if (!current && line.quantity > 0) {
      current = {
        ...line,
        quantity: 0,
        source_line_ids: [],
        has_unserved: false
      };
      grouped.set(key, current);
    }
    if (!current) {
      continue;
    }
    current.quantity += line.quantity;
    if (line.quantity > 0) {
      current.source_line_ids.push(line.id);
      current.has_unserved ||= !Boolean(line.served);
    }
  }

  return [...grouped.values()]
    .filter((line) => line.quantity > 0)
    .map(({ has_unserved, ...line }) => ({
      ...line,
      served: has_unserved ? 0 : 1
    }));
}

export function orderLinesForEvent(orders: Orders | null, eventId: number): OrderLine[] {
  return orders?.order_items
    .filter((line) => line.event_id === eventId)
    .sort((a, b) => a.id - b.id) ?? [];
}

export function orderEvents(orders: Orders | null, orderId: number): OrderEvent[] {
  return (
    orders?.order_events
      .filter((event) => event.order_id === orderId)
      .sort((a, b) => b.created_at - a.created_at) ?? []
  );
}

export function printerName(printer: PrinterConfig): string {
  return printer.nickname.trim() || printer.device_name.trim() || t("Εκτυπωτής {0}", printer.id);
}

export function settingsMap(settings: SettingRow[]): Record<string, string> {
  return Object.fromEntries(settings.map((setting) => [setting.key, setting.value]));
}

export function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
